import { getData } from 'conversations-async-data/async-data/operators/getters';
import { useFetchUserInfo } from 'conversations-thread-data/auth/public/hooks';
import { useInboxes } from 'conversations-thread-data/inboxes/public/hooks';
import { canKeepAssigneeOnNewInbox } from 'conversations-thread-data/move-to-inbox/public/operators';
import { useCallback, useMemo } from 'react';
import { useRoutableUsers } from 'routing-ui/users/hooks/useRoutableUsers';
import { getClickedMoveToInboxAssignToDropdown, getClickedMoveToInboxAssignToDropdownOption } from '../../usage-tracking/utils/threadInteraction';
import { getRoutingRuleTypeForEventTracking } from '../_internal/operators/getRoutingRuleTypeForEventTracking';
import { UserInfo as UserInfoRecord } from 'conversations-internal-schema/users-and-teams/records/UserInfo';
import { isInboxHelpdesk } from 'conversations-internal-schema/inbox/operators/inboxGetters';
import { CONVERSATION, TICKET } from 'customer-data-objects/constants/ObjectTypes';
import { ROUTING_SOURCE } from 'routing-ui/shared/constants/routing-source';

// @ts-expect-error module not typed
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
const useRoutableUsersConfig = ({
  inboxId,
  userInfo
}) => {
  const {
    asyncInboxes
  } = useInboxes(true);
  const inboxData = getData(asyncInboxes);
  const inbox = useMemo(() => {
    if (inboxData) {
      return inboxData.find(({
        id
      }) => id === inboxId);
    }
    return null;
  }, [inboxId, inboxData]);
  const specificUsers = useMemo(() => userInfo ? [userInfo.user.user_id] : [], [userInfo]);
  if (inbox) {
    let objectTypeId = CONVERSATION;
    let routingSource = ROUTING_SOURCE.INBOX;
    if (isInboxHelpdesk(inbox)) {
      objectTypeId = TICKET;
      routingSource = ROUTING_SOURCE.HELP_DESK;
    }
    return {
      objectTypeId,
      routingSource,
      specificUsers,
      workspaceId: inboxId
    };
  }
  return {
    objecTypeId: '',
    routingSource: '',
    specificUsers,
    workspaceId: undefined
  };
};
export const useRoutingRules = ({
  assigneeId,
  selectedInboxId,
  routingRuleDefinition,
  setRoutingRuleDefinition
}) => {
  const {
    data: userInfoData,
    loading: userInfoDataIsLoading
  } = useFetchUserInfo();
  const userInfo = userInfoData === null || userInfoData === void 0 ? void 0 : userInfoData.userInfo;
  const routableUserConfig = useRoutableUsersConfig({
    inboxId: selectedInboxId,
    userInfo
  });
  // NOTE: these users are needed to check that the "Keep assignee" option can be
  // used for the current assignee
  const {
    users: routableUsers
  } = useRoutableUsers(routableUserConfig);
  const handleRoutingRuleOnChange = useCallback(routingRule => {
    const type = getRoutingRuleTypeForEventTracking(routingRule);
    trackEvent(getClickedMoveToInboxAssignToDropdownOption({
      type
    }));
    setRoutingRuleDefinition(routingRule);
  }, [setRoutingRuleDefinition]);
  const handleRoutingRuleOnOpen = useCallback(evt => {
    const isOpen = evt.target.value;
    if (isOpen) {
      trackEvent(getClickedMoveToInboxAssignToDropdown());
    }
  }, []);
  const baseRoutingRulesProps = useMemo(() => ({
    currentUser: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.user) || new UserInfoRecord(),
    inboxId: routableUserConfig.workspaceId,
    isLoadingUsersAndTeams: userInfoDataIsLoading,
    objectTypeId: routableUserConfig.objectTypeId,
    onRuleUpdate: handleRoutingRuleOnChange,
    routingRules: routingRuleDefinition,
    routingSource: routableUserConfig.routingSource
  }), [handleRoutingRuleOnChange, routingRuleDefinition, routableUserConfig.objectTypeId, routableUserConfig.routingSource, routableUserConfig.workspaceId, userInfoDataIsLoading, userInfo]);
  const canKeepAssignee = useMemo(() => canKeepAssigneeOnNewInbox({
    assigneeId,
    teamUsersResponse: routableUsers
  }), [assigneeId, routableUsers]);
  return {
    handleRoutingRuleOnOpen,
    baseRoutingRulesProps,
    userInfoDataIsLoading,
    canKeepAssignee
  };
};